import { useMemo, useState } from 'react';

import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Assignment,
  FormatListBulleted,
  PeopleAlt,
  Reply,
} from '@material-ui/icons';
import ImageIcon from '@material-ui/icons/Image';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import MicIcon from '@material-ui/icons/Mic';
import PersonIcon from '@material-ui/icons/Person';
import RoomIcon from '@material-ui/icons/Room';
import VideocamIcon from '@material-ui/icons/Videocam';

import { ReactComponent as InteractiveIcon } from 'assets/images/add-interactive-message.svg';
import AudioMessage from 'components/AudioMessage';
import CardItem from 'components/CardItem';
import CommentMessage from 'components/CommentMessage';
import ContatoMessage from 'components/ContatoMessage';
import DocumentMessage from 'components/DocumentMessage';
import EventMessage from 'components/EventMessage';
import ImageMessage from 'components/ImageMessage';
import LocationMessage from 'components/LocationMessage';
import TextMessage from 'components/TextMessage';
import VideoMessage from 'components/VideoMessage';
import { useSearchQuery } from 'contexts/SearchQueryContext';
import { useAppDispatch } from 'store';
import {
  fetchMensagemCitada,
  MensagemInterface,
  TiposMensagemEnum,
} from 'store/reducers/mensagens';
import getUsuarioNomeExibicao from 'utils/getUsuarioNomeExibicao';
import { getMentionColors } from 'utils/mentionColors';
import { renderTextWithMentions } from 'utils/renderTextWithMentions';
/** Tipos de mensagem */

import CtaUrlButtonMessage from './CtaUrlButtonMessage';
import DialogHeader from './DialogHeader';
import IASummaryMessage from './IASummaryMessage';
import InteractiveListMessage from './InteractiveListMessage';
import MultiContatoMessage from './MultiContatoMessage';
// import PendingMediaMessage from './PendingMediaMessage';
import ReplyButtonsMessage from './ReplyButtonsMessage';
import TemplateMessage from './TemplateMessage';
/** */

type ResponseMessageProps = {
  mensagem: MensagemInterface;
  mensagemCitada: MensagemInterface;
  fromMe?: boolean;
};

const MensagemTipo = {
  [TiposMensagemEnum.AUDIO]: AudioMessage,
  [TiposMensagemEnum.COMENTARIO]: CommentMessage,
  [TiposMensagemEnum.CONTATO]: ContatoMessage,
  [TiposMensagemEnum.DOCUMENTO]: DocumentMessage,
  [TiposMensagemEnum.EVENTO]: EventMessage,
  [TiposMensagemEnum.TEXTO]: TextMessage,
  [TiposMensagemEnum.IMAGEM]: ImageMessage,
  [TiposMensagemEnum.LOCALIZACAO]: LocationMessage,
  [TiposMensagemEnum.VIDEO]: VideoMessage,
  [TiposMensagemEnum.TEMPLATE_WHATSAPP]: TemplateMessage,
  [TiposMensagemEnum.WPP_CTA]: CtaUrlButtonMessage,
  [TiposMensagemEnum.WPP_REPLY_BTN]: ReplyButtonsMessage,
  [TiposMensagemEnum.WPP_REPLY_BTN_RESP]: TextMessage,
  [TiposMensagemEnum.WPP_LIST]: InteractiveListMessage,
  [TiposMensagemEnum.WPP_LIST_RESP]: TextMessage,
  [TiposMensagemEnum.ARRAY_CONTATOS]: MultiContatoMessage,
  [TiposMensagemEnum.RESUMO_IA]: IASummaryMessage,
  // [TiposMensagemEnum.MIDIA]: PendingMediaMessage,
};

const useStyles = makeStyles(({ palette, spacing }) => ({
  root: {
    display: 'flex',
    marginBottom: spacing(1),
  },
  dialogContent: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  titulo: {
    color: palette.text.primary,
  },
  texto: {
    display: '-webkit-box',
    maxHeight: '60px',
    overflow: 'hidden',
    lineHeight: '20px',
    textOverflow: 'ellipsis',
    wordWrap: 'break-word',
    whiteSpace: 'pre-wrap',
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
    color: palette.text.secondary,
    '& a.mention': {
      borderRadius: 4,
      ...getMentionColors(palette),
      paddingInline: 4,
      fontWeight: 500,
      textDecoration: 'none',
      cursor: 'inherit',
      pointerEvents: 'none',
    },
  },
}));

export default function ResponseMessage(props: ResponseMessageProps) {
  const {
    mensagem: { id: mensagemId, grupoChatId },
    mensagemCitada,
    fromMe,
  } = props;
  const classes = useStyles({ fromMe });
  const [open, setOpen] = useState(false);
  const dispatch = useAppDispatch();
  const [mensagemCitadaRedux, setMensagemCitadaRedux] = useState<
    MensagemInterface | undefined
  >(undefined);
  const [loadingMensagemCitada, setLoadingMensagemCitada] = useState(false);
  const searchQuery = useSearchQuery();

  const handleClose = () => {
    setOpen(false);
  };

  const autorMensagem = useMemo(() => {
    if (!mensagemCitadaRedux) return '';

    const { autor, usuario, contato } = mensagemCitadaRedux;

    switch (autor) {
      case 'ATENDENTE':
        return getUsuarioNomeExibicao(usuario) || 'Atendente';
      case 'ATENDENTE_EXTERNO':
        return '';
      case 'CONTATO':
        return contato?.nome || 'Contato';
      case 'BOT':
        return 'Bot';
      default:
        return '';
    }
  }, [mensagemCitadaRedux]);

  const renderIconTipoMensagem = useMemo(() => {
    switch (mensagemCitada.tipo) {
      case TiposMensagemEnum.IMAGEM:
        return <ImageIcon />;
      case TiposMensagemEnum.VIDEO:
        return <VideocamIcon />;
      case TiposMensagemEnum.CONTATO:
        return <PersonIcon />;
      case TiposMensagemEnum.AUDIO:
        return <MicIcon />;
      case TiposMensagemEnum.DOCUMENTO:
        return <InsertDriveFileIcon />;
      case TiposMensagemEnum.LOCALIZACAO:
        return <RoomIcon />;
      case TiposMensagemEnum.WPP_CTA:
        return <InteractiveIcon />;
      case TiposMensagemEnum.TEMPLATE_WHATSAPP:
        return <Assignment />;
      case TiposMensagemEnum.ARRAY_CONTATOS:
        return <PeopleAlt />;
      case TiposMensagemEnum.WPP_LIST:
        return <FormatListBulleted />;
      case TiposMensagemEnum.WPP_REPLY_BTN:
        return <Reply />;
      default:
        return null;
    }
  }, [mensagemCitada.tipo]);

  const renderMensagemCitadaPorTipo = useMemo(() => {
    if (!mensagemCitadaRedux) return null;

    const MessageComponent = MensagemTipo[mensagemCitadaRedux.tipo];

    if (MessageComponent) {
      const { autor, mensagemApagadaEm, isEncaminhada } = mensagemCitadaRedux;
      const fromMe = autor !== 'CONTATO';

      return (
        <MessageComponent
          fromMe={fromMe}
          autor={autorMensagem}
          apagada={Boolean(mensagemApagadaEm)}
          encaminhada={isEncaminhada}
          mensagem={mensagemCitadaRedux}
          mensagemCitada={mensagemCitadaRedux.mensagemCitada}
          verMensagemCitadaNoModal
          onReenviarMensagem={undefined}
          actions={false}
          isGroup={Boolean(grupoChatId)}
        />
      );
    }

    return null;
  }, [autorMensagem, mensagemCitadaRedux, grupoChatId]);

  const mensagensInterativas: Partial<Record<TiposMensagemEnum, string>> = {
    [TiposMensagemEnum.WPP_CTA]: 'Mensagem de CTA',
    [TiposMensagemEnum.WPP_REPLY_BTN]: 'Botão interativo',
    [TiposMensagemEnum.WPP_REPLY_BTN_RESP]: 'Resposta de botão interativo',
    [TiposMensagemEnum.WPP_LIST]: 'Lista interativa',
    [TiposMensagemEnum.WPP_LIST_RESP]: 'Resposta de lista interativa',
    [TiposMensagemEnum.TEMPLATE_WHATSAPP]: 'Mensagem de template',
    [TiposMensagemEnum.ARRAY_CONTATOS]: 'Contatos',
  };

  return (
    <>
      <CardItem
        className={classes.root}
        isQuote
        fromMe={fromMe}
        onSelect={() => setOpen(true)}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" flexDirection="column">
            <Typography variant="caption" className={classes.titulo}>
              VER MENSAGEM RESPONDIDA
            </Typography>

            <Typography
              variant="caption"
              className={classes.texto}
              dangerouslySetInnerHTML={{
                __html: renderTextWithMentions(
                  (mensagemCitada.tipo === TiposMensagemEnum.TEXTO
                    ? mensagemCitada.mensagem
                    : mensagensInterativas[mensagemCitada.tipo] ||
                      mensagemCitada?.tipo.toLowerCase()) || '',
                  mensagemCitada.contatosMencionados || [],
                  searchQuery,
                ),
              }}
            />
          </Box>

          {mensagemCitada.tipo !== TiposMensagemEnum.TEXTO && (
            <Box ml={4}>
              <Avatar src={mensagemCitada?.midiaUrl}>
                {renderIconTipoMensagem}
              </Avatar>
            </Box>
          )}
        </Box>
      </CardItem>

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          onEnter: async () => {
            setLoadingMensagemCitada(true);
            try {
              const result = await dispatch(
                fetchMensagemCitada(mensagemId),
              ).unwrap();
              setMensagemCitadaRedux(result);
            } catch (error) {
              //
            } finally {
              setLoadingMensagemCitada(false);
            }
          },
          onExited: () => {
            setMensagemCitadaRedux(undefined);
          },
        }}
      >
        <DialogHeader title="MENSAGEM RESPONDIDA" onClose={handleClose} />

        <Divider />

        <DialogContent className={classes.dialogContent}>
          {loadingMensagemCitada && (
            <Box m={4} textAlign="center">
              <CircularProgress color="inherit" />
            </Box>
          )}

          {!loadingMensagemCitada && !mensagemCitadaRedux && (
            <Box pl={3} pr={3} pt={2} pb={1}>
              <Typography variant="body2" color="textSecondary" paragraph>
                Mensagem não encontrada
              </Typography>
            </Box>
          )}

          {!loadingMensagemCitada &&
            mensagemCitadaRedux &&
            renderMensagemCitadaPorTipo}
        </DialogContent>
      </Dialog>
    </>
  );
}
